import { Auth, User as AuthUser } from '@grammarly/http-clients-auth'
import { logError, LogErrorMetrics } from '../../tracking/felog'
import { transformToObject } from '../../utils/error'
import { FacadeService } from '../facade'
import { User } from './user'

enum PayloadState {
  Empty = 'empty',
  Success = 'success',
  Pending = 'pending',
  Error = 'error',
}
interface Payload {
  state: PayloadState
  payload: Promise<AuthUser> | undefined
}

const emptyPayload: Payload = {
  state: PayloadState.Empty,
  payload: undefined,
}

export class UserService {
  currentUser: User
  userPayload: Payload

  constructor(
    private api: Auth,
    facadeService: FacadeService
  ) {
    const { user: userData } = facadeService.get()
    this.currentUser = User.create(userData)
    this.userPayload = emptyPayload
  }

  async initialLoad(): Promise<User> {
    if (this.currentUser.isNone) {
      try {
        this.currentUser = await this.load()
      } catch (e) {
        logError('Error loading User at [initialLoad]', LogErrorMetrics.UserServiceMethodFailed, {
          error: transformToObject(e as Error),
        })
      }
    }

    return this.currentUser
  }

  private async load(customFields?: string[]): Promise<User> {
    if (this.userPayload.state !== PayloadState.Pending) {
      this.setPayload({
        state: PayloadState.Pending,
        payload: this.api.orAnonymous(...(customFields || [])),
      })
    }

    try {
      const userData = await this.userPayload.payload
      this.setPayload({ state: PayloadState.Success })
      return User.create(userData)
    } catch (e) {
      this.setPayload({ state: PayloadState.Error })
      throw e
    }
  }

  private setPayload(payload: Partial<Payload>) {
    this.userPayload = {
      ...this.userPayload,
      ...payload,
    }
  }

  setUser = (user: User) => {
    this.currentUser = user
  }

  get isLoaded() {
    return this.userPayload.state === PayloadState.Success
  }
}

export { User }
export * from './context'
